import type {
  AuthenticatedUserDto,
  LoginMembershipDto,
  LoginResponseDto,
  MeApiResponseDto,
} from "./auth.api.types";
import type { AppUser, AuthenticatedUser } from "./authenticated-user";
import { emptyEnrichment } from "./authenticated-user";
import {
  AuthProfileError,
  AuthSessionError,
  extractApiErrorMessageFromData,
} from "./auth.errors";

export interface LoginSession {
  accessToken: string;
  refreshToken: string;
  requiresRestaurantSelection: boolean;
  loginTicket: string | null;
  memberships: LoginMembershipDto[];
}

export function parseLoginResponse(data: unknown): LoginSession {
  if (!data || typeof data !== "object") {
    throw new AuthSessionError("Invalid login response.");
  }

  const apiErrorMessage = extractApiErrorMessageFromData(data);
  if (apiErrorMessage) {
    throw new AuthSessionError(apiErrorMessage);
  }

  const dto = data as Partial<LoginResponseDto>;
  if (!dto.accessToken || typeof dto.accessToken !== "string") {
    throw new AuthSessionError("Login response missing access token.");
  }
  if (!dto.refreshToken || typeof dto.refreshToken !== "string") {
    throw new AuthSessionError("Login response missing refresh token.");
  }
  return {
    accessToken: dto.accessToken,
    refreshToken: dto.refreshToken,
    requiresRestaurantSelection: Boolean(dto.requiresRestaurantSelection),
    loginTicket: dto.loginTicket ?? null,
    memberships: Array.isArray(dto.memberships) ? dto.memberships : [],
  };
}

export function assertMeSuccess(
  response: MeApiResponseDto
): asserts response is MeApiResponseDto & { success: true; resultCode: 200; data: AuthenticatedUserDto } {
  if (!response?.success || response.resultCode !== 200) {
    throw new AuthProfileError(response?.message ?? "Failed to load user profile.");
  }
  if (!isValidAuthenticatedUserDto(response.data)) {
    throw new AuthProfileError("Profile response is missing required identity fields.");
  }
}

function isValidAuthenticatedUserDto(data: unknown): data is AuthenticatedUserDto {
  if (!data || typeof data !== "object") return false;
  const d = data as Partial<AuthenticatedUserDto>;
  return (
    typeof d.userId === "string" &&
    d.userId.length > 0 &&
    typeof d.firstName === "string" &&
    typeof d.lastName === "string" &&
    typeof d.displayName === "string" &&
    typeof d.email === "string" &&
    Array.isArray(d.roles) &&
    typeof d.tokenVersion === "number"
  );
}

export function mapAuthenticatedUserDto(dto: AuthenticatedUserDto): AuthenticatedUser {
  return {
    userId: dto.userId,
    firstName: dto.firstName,
    lastName: dto.lastName,
    displayName: dto.displayName,
    email: dto.email,
    restaurantId: dto.restaurantId,
    roles: [...dto.roles],
    tokenVersion: dto.tokenVersion,
  };
}

export function toAppUser(
  identity: AuthenticatedUser,
  enrichment: Partial<AppUser["enrichment"]> = {}
): AppUser {
  const base = emptyEnrichment();
  return {
    identity,
    enrichment: {
      addresses: enrichment.addresses ?? base.addresses,
      mobileNo: enrichment.mobileNo ?? base.mobileNo,
      dateOfBirth: enrichment.dateOfBirth ?? base.dateOfBirth,
    },
  };
}

export function identityEquals(a: AuthenticatedUser, b: AuthenticatedUser): boolean {
  return (
    a.userId === b.userId &&
    a.tokenVersion === b.tokenVersion &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.displayName === b.displayName &&
    a.email === b.email &&
    a.restaurantId === b.restaurantId &&
    a.roles.length === b.roles.length &&
    a.roles.every((role, i) => role === b.roles[i])
  );
}

export function enrichmentAddressesEqual(
  a: AppUser["enrichment"]["addresses"],
  b: AppUser["enrichment"]["addresses"]
): boolean {
  if (a.length !== b.length) return false;
  return a.every((addr, i) => addr.address_id === b[i]?.address_id);
}
